import React from "react";
import { cn } from "@/lib/utils";

interface DiffViewerProps {
  oldText: string;
  newText: string;
  splitView?: boolean;
  leftTitle?: string;
  rightTitle?: string;
  className?: string;
}

const DiffViewer = ({
  oldText,
  newText,
  splitView = true,
  leftTitle = "Previous Version",
  rightTitle = "New Version",
  className,
}: DiffViewerProps) => {
  // Function to generate diff with redlining and bluelining
  const generateInlineDiff = (oldStr: string, newStr: string) => {
    // Simple diff algorithm
    const oldWords = oldStr.split(/\s+/);
    const newWords = newStr.split(/\s+/);

    // Create a map of words for faster lookup
    const oldWordsMap = new Map();
    oldWords.forEach((word, index) => {
      if (!oldWordsMap.has(word)) {
        oldWordsMap.set(word, []);
      }
      oldWordsMap.get(word).push(index);
    });

    // Track which words have been matched
    const matchedOld = new Array(oldWords.length).fill(false);
    const matchedNew = new Array(newWords.length).fill(false);

    // Find exact matches first
    for (let i = 0; i < newWords.length; i++) {
      const word = newWords[i];
      if (oldWordsMap.has(word)) {
        const positions = oldWordsMap.get(word);
        for (const pos of positions) {
          if (!matchedOld[pos]) {
            matchedOld[pos] = true;
            matchedNew[i] = true;
            break;
          }
        }
      }
    }

    // Generate the HTML with redlining and bluelining
    const result = [];

    // Add deleted words (redlining)
    for (let i = 0; i < oldWords.length; i++) {
      if (!matchedOld[i]) {
        result.push(
          <span key={`old-${i}`} className="text-danger line-through">
            {oldWords[i]}{" "}
          </span>,
        );
      }
    }

    // Add a separator
    if (result.length > 0) {
      result.push(<span key="separator" className="mx-2"></span>);
    }

    // Add added words (bluelining)
    for (let i = 0; i < newWords.length; i++) {
      if (!matchedNew[i]) {
        result.push(
          <span
            key={`new-${i}`}
            className="text-foreground font-medium bg-secondary/30"
          >
            {newWords[i]}{" "}
          </span>,
        );
      }
    }

    // Add unchanged words
    for (let i = 0; i < newWords.length; i++) {
      if (matchedNew[i]) {
        result.push(<span key={`unchanged-${i}`}>{newWords[i]} </span>);
      }
    }

    return result;
  };

  // Function to generate side-by-side diff
  const generateSplitDiff = () => {
    return (
      <div className="grid grid-cols-2 gap-4">
        <div className="border rounded-md p-4 bg-muted/30">
          <div className="text-sm font-medium mb-2">{leftTitle}</div>
          <pre className="whitespace-pre-wrap font-mono text-sm">{oldText}</pre>
        </div>
        <div className="border rounded-md p-4">
          <div className="text-sm font-medium mb-2">{rightTitle}</div>
          <pre className="whitespace-pre-wrap font-mono text-sm">{newText}</pre>
        </div>
      </div>
    );
  };

  // Function to generate a more detailed inline diff
  const generateDetailedInlineDiff = () => {
    // Split text into paragraphs
    const oldParagraphs = oldText.split(/\n\n+/);
    const newParagraphs = newText.split(/\n\n+/);

    // Process each paragraph
    const diffElements = [];

    // Simple approach: show deleted paragraphs, then added paragraphs
    const maxLength = Math.max(oldParagraphs.length, newParagraphs.length);

    for (let i = 0; i < maxLength; i++) {
      const oldPara = i < oldParagraphs.length ? oldParagraphs[i] : "";
      const newPara = i < newParagraphs.length ? newParagraphs[i] : "";

      if (oldPara === newPara) {
        // Unchanged paragraph
        diffElements.push(
          <div key={`para-${i}`} className="mb-4">
            <p className="whitespace-pre-wrap font-mono text-sm">{oldPara}</p>
          </div>,
        );
      } else {
        // Changed paragraph
        if (oldPara) {
          diffElements.push(
            <div key={`para-old-${i}`} className="mb-2">
              <p className="whitespace-pre-wrap font-mono text-sm text-danger line-through">
                {oldPara}
              </p>
            </div>,
          );
        }

        if (newPara) {
          diffElements.push(
            <div key={`para-new-${i}`} className="mb-4">
              <p className="whitespace-pre-wrap font-mono text-sm text-foreground bg-secondary/30 p-1 rounded">
                {newPara}
              </p>
            </div>,
          );
        }
      }
    }

    return diffElements;
  };

  return (
    <div className={cn("diff-viewer", className)}>
      {splitView ? (
        generateSplitDiff()
      ) : (
        <div className="border rounded-md p-4">
          <div className="text-sm font-medium mb-4">Changes</div>
          <div>{generateDetailedInlineDiff()}</div>
        </div>
      )}
    </div>
  );
};

export { DiffViewer };
